/**
 * AnimatedScoreNumber - Spring-animated score counter
 *
 * Animates the score number counting to its final value using
 * spring physics. Falls back to static display on web or when
 * prefers-reduced-motion is enabled.
 *
 * Features:
 * - Smooth spring animation (~400ms) between score values
 * - Green color flash during score improvement (lower = better)
 * - Haptic feedback on improvement (mobile only)
 * - Shows 1 decimal during animation, integer when settled
 * - Respects reduced motion preference
 *
 * Uses useAnimatedReaction to bridge animation thread back to
 * React state via runOnJS, updating the displayed text on each frame.
 *
 * @module components/modern-results/AnimatedScoreNumber
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';
import {
  useSharedValue,
  withSpring,
  runOnJS,
  useAnimatedReaction,
} from 'react-native-reanimated';
import { ImpactFeedbackStyle } from 'expo-haptics';
import { TierHeadline } from '@dramadetector/ui';
import { triggerImpact } from '@/lib/haptics';
import { SCORE_SPRING_CONFIG, scoreDisplayStyles } from './animation-constants';

/** Green used during score improvement (matches ScoreDeltaIndicator) */
const IMPROVEMENT_GREEN = '#16A34A';

interface AnimatedScoreNumberProps {
  /** Target score value (0-10) */
  value: number | null;
  /** Whether to skip animation (reduced motion) */
  isReducedMotion?: boolean;
  /** Whether the score is dimmed (e.g. rescoring in progress) */
  isDimmed?: boolean;
  /** Test ID */
  testID?: string;
}

/**
 * Format the animated score value for display.
 * During animation (value not settled), show 1 decimal.
 * When settled (close to an integer or final value), show standard 1 decimal format.
 */
function formatAnimatedScore(animatedVal: number, targetVal: number): string {
  const isSettled = Math.abs(animatedVal - targetVal) < 0.05;
  if (isSettled) {
    return targetVal.toFixed(1);
  }
  return animatedVal.toFixed(1);
}

export function AnimatedScoreNumber({
  value,
  isReducedMotion,
  isDimmed,
  testID = 'animated-score-number',
}: AnimatedScoreNumberProps) {
  const [displayValue, setDisplayValue] = useState(value ?? 0);
  const [isImproving, setIsImproving] = useState(false);
  const animatedValue = useSharedValue(value ?? 0);
  const prevValueRef = useRef(value ?? 0);
  const improvingTimerRef = useRef<NodeJS.Timeout | null>(null);

  const updateDisplay = useCallback((v: number) => {
    setDisplayValue(v);
  }, []);

  // Bridge animated value changes back to React state
  useAnimatedReaction(
    () => animatedValue.value,
    (current) => {
      runOnJS(updateDisplay)(current);
    }
  );

  useEffect(() => {
    if (value === null) return;

    const prevVal = prevValueRef.current;
    const improving = value < prevVal;

    // Trigger improvement effects when score decreases (lower = better)
    if (improving && prevVal !== 0) {
      setIsImproving(true);
      triggerImpact(ImpactFeedbackStyle.Light);

      // Clear any pending timer
      if (improvingTimerRef.current) {
        clearTimeout(improvingTimerRef.current);
      }

      // Reset color after animation settles (~600ms to be safe)
      improvingTimerRef.current = setTimeout(() => {
        setIsImproving(false);
        improvingTimerRef.current = null;
      }, 600);
    }

    prevValueRef.current = value;

    if (isReducedMotion) {
      animatedValue.value = value;
    } else {
      animatedValue.value = withSpring(value, SCORE_SPRING_CONFIG);
    }
  }, [value, isReducedMotion, animatedValue]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (improvingTimerRef.current) {
        clearTimeout(improvingTimerRef.current);
      }
    };
  }, []);

  const colorStyle = isImproving
    ? styles.improvingColor
    : undefined;

  return (
    <TierHeadline
      size="4xl"
      weight="bold"
      color="text-white"
      as="h1"
      style={[
        scoreDisplayStyles.scoreText,
        isDimmed && styles.dimmed,
        colorStyle,
      ]}
      testID={testID}
    >
      {value !== null ? formatAnimatedScore(displayValue, value) : '--'}
    </TierHeadline>
  );
}

const styles = StyleSheet.create({
  improvingColor: {
    color: IMPROVEMENT_GREEN,
  },
  dimmed: {
    opacity: 0.5,
  },
});
